/**
 * v2 InlineJobTracker — sticky bottom on the chassis Inventory tab.
 *
 * Per goal b: pending jobs against this chassis are viewable here
 * with three expand levels:
 *
 *   L1 (collapsed): status pill | name | progress | elapsed | ✕
 *   L2 (transcript tail): last 5 transcript rows tick live
 *   L3 (results): per-target results table
 *
 * Listens for the ``inline-job-tracker.add`` custom DOM event so any
 * v2 page that enqueues a job hands it off here (same pattern as v1,
 * see VdslPortsCard:99). Tracked IDs live in component state; the
 * operator dismisses via ✕ but the job continues running in the
 * worker.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { api } from "../../../api/client";
import { LocalTime } from "../../../lib/datetime";
import { Badge, type BadgeTone } from "../../components/Badge";

type JobStatus =
  | "queued"
  | "queued-on-chassis"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "partial";

type JobOut = {
  id: string;
  chassis_id: number | null;
  rendered_name: string;
  status: JobStatus;
  started_at: string | null;
  completed_at: string | null;
  created_at: string | null;
  total_targets: number | null;
  completed_targets: number | null;
};

type TranscriptRow = {
  id: number;
  ts: string;
  direction: "command" | "response" | "status" | "error";
  content: string;
};

type TranscriptResponse = {
  rows: TranscriptRow[];
  next_since_id: number | null;
};

type JobResultsResponse = {
  job: JobOut;
  results: {
    id: number;
    target: string;
    status: string;
    parsed_data: Record<string, unknown> | null;
    executed_at: string;
  }[];
};

const STATUS_TONE: Record<JobStatus, BadgeTone> = {
  queued: "neutral",
  "queued-on-chassis": "neutral",
  running: "info",
  succeeded: "success",
  failed: "danger",
  cancelled: "muted",
  partial: "warning",
};

const TERMINAL_STATES = new Set<JobStatus>([
  "succeeded",
  "failed",
  "cancelled",
  "partial",
]);

// Clear-class jobs the worker auto-chains follow-ups after (trigger
// ``post_clear_chain``): a background list_pending_onus re-scan + a
// background backup_running_config (whose inline reparse refreshes the
// parsed profiles). Those rows are background=true so the operator jobs
// list never shows them — render them here instead, as a pipeline
// connected to the job that triggered them.
const CHAIN_TRIGGER_NAMES = new Set([
  "deprovision_onu",
  "deprovision_onu_batch",
  "bulk_pon_clear",
  "move_onu",
  "swap_onu",
]);

const CHAIN_STEPS = [
  {
    name: "list_pending_onus",
    active: "Discovering…",
    done: "Discovered",
    failed: "Discovery",
  },
  {
    name: "backup_running_config",
    active: "Refreshing profiles…",
    done: "Profiles refreshed",
    failed: "Profile refresh",
  },
] as const;

const CHAIN_STEP_NAMES = new Set<string>(CHAIN_STEPS.map((s) => s.name));

/**
 * Newest follow-up job per chain step, keyed by rendered_name. Only
 * rows created at/after the trigger's completion count (minus a small
 * skew window so a dedupe-reused scan queued moments earlier still
 * attaches).
 */
function pickChainJobs(jobs: JobOut[], trigger: JobOut): Map<string, JobOut> {
  const sinceMs = trigger.completed_at
    ? new Date(trigger.completed_at).getTime() - 60_000
    : 0;
  const picked = new Map<string, JobOut>();
  for (const j of jobs) {
    if (!CHAIN_STEP_NAMES.has(j.rendered_name)) continue;
    if (!j.created_at || new Date(j.created_at).getTime() < sinceMs) continue;
    const prev = picked.get(j.rendered_name);
    if (
      !prev ||
      new Date(prev.created_at ?? 0).getTime() <
        new Date(j.created_at).getTime()
    ) {
      picked.set(j.rendered_name, j);
    }
  }
  return picked;
}

type ExpandLevel = 0 | 1 | 2;

function elapsedStr(started: string | null, completed: string | null): string {
  if (!started) return "—";
  const start = new Date(started).getTime();
  const end = completed ? new Date(completed).getTime() : Date.now();
  const sec = Math.max(0, Math.round((end - start) / 1000));
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m${sec % 60}s`;
}

export function InlineJobTracker() {
  const [tracked, setTracked] = useState<string[]>([]);
  const [levels, setLevels] = useState<Record<string, ExpandLevel>>({});

  // Listen for hand-off events from other v2 components.
  useEffect(() => {
    const onAdd = (event: Event) => {
      const e = event as CustomEvent<{ jobId?: string }>;
      const id = e.detail?.jobId;
      if (!id) return;
      setTracked((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setLevels((prev) => ({ ...prev, [id]: 0 }));
    };
    window.addEventListener("inline-job-tracker.add", onAdd as EventListener);
    return () => window.removeEventListener("inline-job-tracker.add", onAdd as EventListener);
  }, []);

  const remove = useCallback((id: string) => {
    setTracked((prev) => prev.filter((x) => x !== id));
    setLevels((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const cycleLevel = useCallback((id: string) => {
    setLevels((prev) => {
      const cur = prev[id] ?? 0;
      const next = ((cur + 1) % 3) as ExpandLevel;
      return { ...prev, [id]: next };
    });
  }, []);

  if (tracked.length === 0) {
    return null;
  }

  return (
    <section className="sticky bottom-0 z-30 flex flex-col gap-1.5 rounded-lg border border-emerald-800 bg-slate-950/95 p-2 backdrop-blur">
      <div className="px-1 text-[10px] uppercase tracking-wide text-emerald-400">
        Tracked jobs ({tracked.length})
      </div>
      {tracked.map((id) => (
        <Row
          key={id}
          jobId={id}
          level={levels[id] ?? 0}
          onCycle={() => cycleLevel(id)}
          onClose={() => remove(id)}
        />
      ))}
    </section>
  );
}

function Row({
  jobId,
  level,
  onCycle,
  onClose,
}: {
  jobId: string;
  level: ExpandLevel;
  onCycle: () => void;
  onClose: () => void;
}) {
  const job = useQuery<JobOut>({
    queryKey: ["v2", "tracker", "job", jobId],
    queryFn: async () => {
      const res = await api.get<JobOut>(`/jobs/${jobId}`);
      return res.data;
    },
    // Poll while running; back off once terminal.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      if (s && TERMINAL_STATES.has(s)) return false;
      return 2_000;
    },
  });

  const status = job.data?.status ?? "queued";
  const isTerminal = TERMINAL_STATES.has(status);
  const progress =
    job.data?.total_targets && job.data.total_targets > 0
      ? `${job.data.completed_targets ?? 0}/${job.data.total_targets}`
      : "";

  return (
    <div className="rounded bg-slate-900 shadow-border">
      {/* L1 row */}
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button
          type="button"
          onClick={onCycle}
          className="rounded text-slate-500 hover:text-slate-200"
          title={
            level === 0
              ? "Show transcript tail"
              : level === 1
              ? "Show per-target results"
              : "Collapse"
          }
        >
          {level > 0 ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </button>
        <Badge tone={STATUS_TONE[status]}>{status}</Badge>
        <span className="font-mono text-xs text-slate-200">
          {job.data?.rendered_name ?? jobId.slice(0, 8)}
        </span>
        {progress && (
          <span className="text-[10px] text-slate-500">{progress}</span>
        )}
        <span className="text-[10px] text-slate-500">
          {elapsedStr(job.data?.started_at ?? null, job.data?.completed_at ?? null)}
        </span>
        <span className="flex-1" />
        {!isTerminal && (
          <span className="text-[10px] text-emerald-400">in progress</span>
        )}
        <button
          type="button"
          onClick={onClose}
          className="rounded text-slate-500 hover:text-slate-200"
          title="Stop tracking (job keeps running)"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Post-clear pipeline — background follow-ups chained by the
          worker, connected to the trigger job they belong to. */}
      {job.data &&
        CHAIN_TRIGGER_NAMES.has(job.data.rendered_name) &&
        (status === "succeeded" || status === "partial") &&
        job.data.chassis_id != null && <ChainPipeline trigger={job.data} />}

      {/* L2 — transcript tail */}
      {level === 1 && <TranscriptTail jobId={jobId} terminal={isTerminal} />}

      {/* L3 — per-target results */}
      {level === 2 && <ResultsTable jobId={jobId} />}
    </div>
  );
}

function TranscriptTail({
  jobId,
  terminal,
}: {
  jobId: string;
  terminal: boolean;
}) {
  const q = useQuery<TranscriptResponse>({
    queryKey: ["v2", "tracker", "transcript", jobId],
    queryFn: async () => {
      // v1 transcripts endpoint is ASC by default; pull a generous
      // page and show the tail. A dedicated ``order=desc&limit=5``
      // shortcut would be slightly nicer but isn't strictly needed.
      const res = await api.get<TranscriptResponse>(
        `/transcripts?job_id=${jobId}&limit=200`,
      );
      return res.data;
    },
    refetchInterval: terminal ? false : 2_000,
  });

  const tail = (q.data?.rows ?? []).slice(-5);
  return (
    <div className="border-t border-slate-800 px-2 py-1.5">
      {q.isLoading ? (
        <p className="text-[10px] text-slate-500">Loading transcript…</p>
      ) : tail.length === 0 ? (
        <p className="text-[10px] text-slate-500">
          No transcript rows yet — worker hasn't sent commands.
        </p>
      ) : (
        <ol className="flex flex-col font-mono text-[10px]">
          {tail.map((r) => (
            <li
              key={r.id}
              className={[
                "px-1 leading-tight",
                r.direction === "command"
                  ? "text-emerald-300"
                  : r.direction === "response"
                  ? "text-slate-400"
                  : r.direction === "error"
                  ? "text-red-300"
                  : "text-amber-300",
              ].join(" ")}
              title={r.direction}
            >
              <span className="text-slate-600">
                {r.direction === "command" ? "» " : r.direction === "response" ? "« " : "· "}
              </span>
              <span className="whitespace-pre-wrap">
                {r.content.length > 200 ? r.content.slice(0, 200) + "…" : r.content}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function ResultsTable({ jobId }: { jobId: string }) {
  const q = useQuery<JobResultsResponse>({
    queryKey: ["v2", "tracker", "results", jobId],
    queryFn: async () => {
      const res = await api.get<JobResultsResponse>(`/jobs/${jobId}/results`);
      return res.data;
    },
    refetchInterval: 5_000,
  });

  const rows = q.data?.results ?? [];
  return (
    <div className="border-t border-slate-800 px-2 py-1.5">
      {q.isLoading ? (
        <p className="text-[10px] text-slate-500">Loading results…</p>
      ) : rows.length === 0 ? (
        <p className="text-[10px] text-slate-500">No per-target results yet.</p>
      ) : (
        <table className="w-full text-[10px]">
          <thead className="text-left uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-1 py-0.5">Target</th>
              <th className="px-1 py-0.5">Status</th>
              <th className="px-1 py-0.5">At</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/40 text-slate-300">
            {rows.slice(0, 30).map((r) => (
              <tr key={r.id}>
                <td className="px-1 py-0.5 font-mono">{r.target}</td>
                <td className="px-1 py-0.5">
                  {r.status === "success" ? (
                    <span className="text-emerald-400">ok</span>
                  ) : (
                    <span className="text-red-400">{r.status}</span>
                  )}
                </td>
                <td className="px-1 py-0.5 font-mono text-slate-500">
                  <LocalTime iso={r.executed_at} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rows.length > 30 && (
        <p className="mt-1 text-[10px] text-slate-500">
          showing 30 of {rows.length} — open the full job page for the rest.
        </p>
      )}
    </div>
  );
}

function ChainPipeline({ trigger }: { trigger: JobOut }) {
  // Background jobs never reach the operator jobs list, so pull the
  // chassis's background rows directly and match the chained follow-ups
  // by name + creation time. Polls until every step is terminal.
  const q = useQuery<{ jobs: JobOut[] }>({
    queryKey: ["v2", "tracker", "chain", trigger.id],
    queryFn: async () => {
      const res = await api.get<{ jobs: JobOut[] }>("/jobs", {
        params: {
          chassis_id: trigger.chassis_id,
          background: true,
          limit: 20,
        },
      });
      return res.data;
    },
    refetchInterval: (query) => {
      const picked = pickChainJobs(query.state.data?.jobs ?? [], trigger);
      const allTerminal =
        picked.size === CHAIN_STEPS.length &&
        [...picked.values()].every((j) => TERMINAL_STATES.has(j.status));
      return allTerminal ? false : 2_000;
    },
  });

  const picked = pickChainJobs(q.data?.jobs ?? [], trigger);
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-800 px-2 py-1 text-[10px]">
      <span className="uppercase tracking-wide text-slate-500">follow-up</span>
      <span className="text-slate-300">{trigger.rendered_name} ✓</span>
      {CHAIN_STEPS.map((step) => {
        const j = picked.get(step.name) ?? null;
        const isDone = j?.status === "succeeded" || j?.status === "partial";
        const isFailed = j?.status === "failed" || j?.status === "cancelled";
        return (
          <span key={step.name} className="flex items-center gap-1.5">
            <span className="text-slate-600">→</span>
            <span
              className={
                isDone
                  ? "text-emerald-400"
                  : isFailed
                    ? "text-red-400"
                    : j
                      ? "text-sky-300"
                      : "text-slate-500"
              }
              title={
                j
                  ? `${step.name} · ${j.status}`
                  : `${step.name} · waiting for the worker to chain it`
              }
            >
              {isDone
                ? step.done
                : isFailed
                  ? `${step.failed} ${j?.status}`
                  : step.active}
            </span>
          </span>
        );
      })}
    </div>
  );
}
